import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface PendingAttachment {
  filename: string;
  mimeType: string;
  base64: string;
  sizeBytes: number;
  /** Display-friendly size label, e.g. "1.3 MB" */
  sizeLabel: string;
}

/** Outcome of the AI field extraction for one uploaded file. */
export interface InvoiceParseStatus {
  filename: string;
  success: boolean;
}

@Component({
  selector: 'app-invoice-upload',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './invoice-upload.component.html',
})
export class InvoiceUploadComponent implements OnDestroy {

  /** Maximum bytes per individual file (2 MB) */
  @Input() maxFileBytes = 2 * 1024 * 1024;

  /** Maximum total bytes across all files (5 MB) */
  @Input() maxTotalBytes = 5 * 1024 * 1024;

  /** Maximum number of files */
  @Input() maxFiles = 5;

  /** When true (controlled by parent), the AI-parsing indicator is shown. */
  @Input() isParsing = false;

  /** Per-file extraction outcome, supplied by the parent once parsing finishes. */
  @Input() parseStatuses: InvoiceParseStatus[] = [];

  /** True when the parse request itself failed, so no file has extracted fields. */
  @Input() parseFailed = false;

  /** Guidance shown when nothing could be extracted; the host decides the fallback. */
  @Input() noResultsHint = 'Enter the invoice details manually below, or try again.';

  /** Guidance shown when only some files could be extracted. */
  @Input() partialResultsHint =
    'The fields below cover only the invoices that were read — add the missing details manually before continuing.';

  /** Emits when a new batch of valid files has been selected (all files in state) */
  @Output() filesChanged = new EventEmitter<PendingAttachment[]>();

  /** Emits the current files so the parent can run the extraction again. */
  @Output() retryParse = new EventEmitter<PendingAttachment[]>();

  pendingFiles: PendingAttachment[] = [];
  validationErrors: string[] = [];
  isDragging = false;
  isProcessing = false;

  private readers: FileReader[] = [];

  get totalBytes(): number {
    return this.pendingFiles.reduce((s, f) => s + f.sizeBytes, 0);
  }

  get failedFilenames(): string[] {
    return this.parseStatuses.filter((s) => !s.success).map((s) => s.filename);
  }

  get parsedCount(): number {
    return this.parseStatuses.filter((s) => s.success).length;
  }

  get hasParseWarning(): boolean {
    return !this.isParsing && (this.parseFailed || this.failedFilenames.length > 0);
  }

  get parseWarningTitle(): string {
    if (this.parseFailed) {
      return 'Automatic extraction is unavailable right now.';
    }
    const failed = this.failedFilenames;
    if (this.parsedCount === 0) {
      return failed.length === 1
        ? `We couldn't read "${failed[0]}".`
        : `We couldn't read any of the ${failed.length} uploaded invoices.`;
    }
    return `We couldn't read ${failed.length} of ${this.parseStatuses.length} invoices: ${failed.join(', ')}.`;
  }

  get parseWarningDetail(): string {
    return this.parsedCount === 0 ? this.noResultsHint : this.partialResultsHint;
  }

  isFileParseFailed(filename: string): boolean {
    return this.parseStatuses.some((s) => s.filename === filename && !s.success);
  }

  onRetryParse(): void {
    this.retryParse.emit([...this.pendingFiles]);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files) {
      this.processFileList(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFileList(Array.from(input.files));
    }
    input.value = '';
  }

  removeFile(index: number): void {
    this.pendingFiles.splice(index, 1);
    this.validationErrors = [];
    this.filesChanged.emit([...this.pendingFiles]);
  }

  clearAll(): void {
    this.pendingFiles = [];
    this.validationErrors = [];
    this.filesChanged.emit([]);
  }

  private processFileList(files: File[]): void {
    this.validationErrors = [];
    const accepted = ['application/pdf', 'image/png', 'image/jpeg'];
    const toAdd: File[] = [];

    for (const file of files) {
      if (!accepted.includes(file.type)) {
        this.validationErrors.push(`"${file.name}" is not a supported type (PDF, PNG, JPEG only).`);
        continue;
      }
      if (file.size > this.maxFileBytes) {
        this.validationErrors.push(`"${file.name}" exceeds the 2 MB per-file limit.`);
        continue;
      }
      if (this.pendingFiles.length + toAdd.length >= this.maxFiles) {
        this.validationErrors.push(`Maximum of ${this.maxFiles} files allowed.`);
        break;
      }
      const projectedTotal = this.totalBytes + toAdd.reduce((s, f) => s + f.size, 0) + file.size;
      if (projectedTotal > this.maxTotalBytes) {
        this.validationErrors.push(`Adding "${file.name}" would exceed the 5 MB total limit.`);
        break;
      }
      toAdd.push(file);
    }

    if (toAdd.length === 0) return;

    this.isProcessing = true;
    let pending = toAdd.length;

    for (const file of toAdd) {
      const reader = new FileReader();
      this.readers.push(reader);
      reader.onload = () => {
        const dataUrl = reader.result as string;
        // Strip the data-URL prefix (data:<mime>;base64,<data>)
        const base64 = dataUrl.split(',')[1] ?? '';
        this.pendingFiles.push({
          filename: file.name,
          mimeType: file.type,
          base64,
          sizeBytes: file.size,
          sizeLabel: this.formatBytes(file.size),
        });
        pending--;
        if (pending === 0) {
          this.isProcessing = false;
          this.filesChanged.emit([...this.pendingFiles]);
        }
      };
      reader.onerror = () => {
        this.validationErrors.push(`Failed to read "${file.name}".`);
        pending--;
        if (pending === 0) {
          this.isProcessing = false;
          this.filesChanged.emit([...this.pendingFiles]);
        }
      };
      reader.readAsDataURL(file);
    }
  }

  private formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  }

  mimeIcon(mimeType: string): string {
    if (mimeType === 'application/pdf') return 'picture_as_pdf';
    if (mimeType.startsWith('image/')) return 'image';
    return 'attach_file';
  }

  ngOnDestroy(): void {
    this.readers.forEach(r => r.abort?.());
    this.readers = [];
  }
}
